<template>
	<client-only>
		<AdaptiveContainer v-if="loading">
			<div class="block w-[1000vw] mt-6 mb-12">
				<div v-for="index in 5" :key="index" class="inline-block float-left w-[320px] mr-10">
					<DjCardJob :loading="true" />
				</div>
			</div>
		</AdaptiveContainer>
		<carousel
			ref="carouselEl"
			v-if="showCarousel && !loading"
			:settings="settings"
			class="pb-5"
			:class="`carousel-dj carousel-jobs carousel-buttons-${buttonType}`"
		>
			<slide
				v-for="(item, index) in carouselData.concat(new Array(helperSlides))"
				:style="{ paddingRight: slideMargin + 'px' }"
			>
				<DjCardCompanyBanner
					v-if="Object.keys(banner).length > 0 && index == 0 && isMobile() == false"
					:subtitle="banner.sub_title ? localized(banner.sub_title) : ''"
					:title="localized(banner.title)"
					:description="localized(banner.content)"
					:cta="localized(banner.cta_text)"
					:url="localized(banner.cta_link)"
					:logo="banner.image.cloudinary"
				/>

				<CardJob
					v-else-if="index < carouselData.length"
					:key="index"
					:job="item"
					:address="useRegionOverride.getAddress(item)"
					newTab
				/>
				<div v-else></div>
			</slide>

			<template v-if="showCarouselAddons()" #addons="{ currentSlide, slidesCount }">
				<div class="carousel__navigation-buttons mx-auto">
					<button
						@click="slidePrev(currentSlide, containerSlideNumber)"
						class="carousel__prev transition-colors"
						:class="{ 'hide-prev': Math.ceil(currentSlide) < 1 }"
					></button>
					<button
						@click="
							slideTo(
								currentSlide + containerSlideNumber,
								slidesCount - helperSlides - containerSlideNumber
							)
						"
						class="carousel__next transition-colors"
						:class="{
							'hide-next': Math.ceil(currentSlide) >= slidesCount - containerSlideNumber - helperSlides,
						}"
					></button>
				</div>

				<div class="hidden sm:block carousel__custom-pagination mx-auto">
					<button
						v-for="(paginationEl, index) in Math.ceil((slidesCount - helperSlides) / containerSlideNumber)"
						:key="index"
						@click="
							slideTo(index * containerSlideNumber, slidesCount - helperSlides - containerSlideNumber)
						"
						class="h-4 w-4 m-2 bg-dj-gray-light rounded-full"
						:class="{
							'dot--active': activePagination(
								index,
								currentSlide,
								slidesCount,
								containerSlideNumber,
								helperSlides
							),
						}"
					></button>
				</div>
			</template>
		</carousel>
		<AdaptiveContainer v-if="showCarousel && !loading && Object.keys(banner).length > 0 && isMobile() == true">
			<DjCardCompanyBanner
				:subtitle="localized(banner.sub_title)"
				:title="localized(banner.title)"
				:description="localized(banner.content)"
				:cta="localized(banner.cta_text)"
				:url="localized(banner.cta_link)"
				:logo="banner.image.cloudinary"
			/>
		</AdaptiveContainer>
		<template #fallback>
			<AdaptiveContainer v-if="!loading && ssrStaticJobs.length > 0">
				<div class="block w-[1000vw] mt-6 mb-12">
					<div
						v-for="(item, index) in ssrStaticJobs"
						:key="item.id || index"
						class="inline-block float-left w-[320px] mr-10"
					>
						<CardJob :job="item" :address="useRegionOverride.getAddress(item)" newTab />
					</div>
				</div>
			</AdaptiveContainer>
			<AdaptiveContainer v-else-if="fetchError">
				<div class="block w-[1000vw] mt-6 mb-12">
					<div v-for="index in 5" :key="index" class="inline-block float-left w-[320px] mr-10">
						<DjCardJob :loading="true" />
					</div>
				</div>
			</AdaptiveContainer>
		</template>
	</client-only>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { Carousel, Slide } from "vue3-carousel";
import { useI18n } from "vue-i18n";
import { type bannerObject } from "@@/types";
import { useRegionOverrides } from "@@/composables/regionOverrides";

// --- Types ---
type LocaleCode = "hu" | "en" | "ro";

type CarouselJob = {
	id?: number;
	slug?: Record<string, string>;
	company?: {
		has_gold?: number;
	};
	[key: string]: unknown;
};

// --- Props & Emits ---
const props = defineProps({
	dataUrl: {
		type: String,
		default: "jobs",
	},
	dataUrlExtra: {
		type: String,
		default: "",
	},
	highlightedGold: {
		type: Boolean,
		default: false,
	},
	slideWidth: {
		type: Number,
		default: 320,
	},
	buttonType: {
		type: String,
		default: "white",
		validator: (value: string) => ["white", "gray"].includes(value),
	},
	rawJobsData: {
		type: Array,
		default: () => [],
	},
	filterJobId: {
		type: Number,
		default: null,
	},
	sortByGold: {
		type: Boolean,
		default: false,
	},
	bannerType: {
		type: String,
		default: "",
	},
	showBanner: {
		type: Boolean,
		default: true,
	},
});

const emit = defineEmits(["loaded", "hasCategoryJobs", "hideSection"]);

// --- Composables ---
const { locale } = useI18n();
const useRegionOverride = useRegionOverrides();
const favJobCategories = useCookie("favouriteJobCategories") || "";
const jobsCache = useState<Record<string, CarouselJob[]>>("carousel-jobs-cache", () => ({}));

// --- State ---
const loading = ref(true);
const fetchError = ref(false);
const carouselEl = ref<{ slideTo: (index: number) => void } | null>(null);
const jobsData = ref<CarouselJob[]>([]);
const showCarousel = ref(false);
const banner = ref({} as bannerObject);

const slideMargin = ref(20);
const helperSlides = ref(0);
const containerSlideNumber = ref(0);

// --- Helpers ---
const activeLocale = computed<LocaleCode>(() => {
	if (locale.value === "en" || locale.value === "ro") return locale.value as LocaleCode;
	return "hu";
});

function getRegionCode() {
	return useRegion.getCurrentRegion().code.toLowerCase() || "hu";
}

function getBodyWidth() {
	if (typeof document === "undefined") return 1280;
	return document.body?.offsetWidth || 1280;
}

let bodyWidth = getBodyWidth();

function isMobile() {
	return getBodyWidth() < 741;
}

function localized(value: unknown) {
	const localeMap = value as Record<string, string> | undefined;
	return localeMap?.[activeLocale.value] || "";
}

function paramJobCategory() {
	let param = "";
	if (favJobCategories && favJobCategories.value && favJobCategories.value.toString().length > 0) {
		favJobCategories.value
			.toString()
			.split(",")
			.forEach((el) => {
				param = param.concat(`&tags[job-category][]=${el}`);
			});
	}
	return param;
}

function getJobsCacheKey() {
	return [
		useRegion.getCurrentRegion().code.toLowerCase(),
		props.bannerType,
		props.dataUrl,
		props.dataUrlExtra,
		String(props.highlightedGold),
		String(globalConfig.isBlue),
		String(globalConfig.isNotBlueHU),
		String(favJobCategories?.value || ""),
	].join("|");
}

// --- Data Fetching ($fetch inside useAsyncData) ---
const { data: fetchedData, error: asyncFetchError } = await useAsyncData(
	`carousel-data-${props.bannerType}-${activeLocale.value}`,
	async () => {
		// Set up temporary variables to hold our fetched data
		let fetchedBanner = {} as bannerObject;
		let fetchedJobs = [] as CarouselJob[];

		// Fetch Banner
		if (props.bannerType.length > 0) {
			const bannerData = await useBanners.getBanner(props.bannerType);
			fetchedBanner = (bannerData || {}) as bannerObject;
		}

		// Fetch Jobs - Check Props First
		if (props.rawJobsData.length > 0) {
			fetchedJobs = props.rawJobsData as CarouselJob[];
			return { banner: fetchedBanner, jobs: fetchedJobs }; // RETURN the data
		}

		const jobsCacheKey = getJobsCacheKey();
		if (jobsCache.value[jobsCacheKey]?.length) {
			fetchedJobs = [...jobsCache.value[jobsCacheKey]];
			return { banner: fetchedBanner, jobs: fetchedJobs }; // RETURN the data
		}

		const url = new URL(`${globalConfig.API}/${props.dataUrl}`);
		const params: Record<string, string> = {
			region: getRegionCode(),
		};

		const hasScopeInDataUrl = (scope: string) => props.dataUrl.includes(`scope[]=${scope}`);

		if (globalConfig.isBlue && !hasScopeInDataUrl("isBlue")) params["scope[]"] = "isBlue";
		if (globalConfig.isNotBlueHU && !hasScopeInDataUrl("isNotBlue")) params["scope[]"] = "isNotBlue";

		// Category Gold Jobs
		if (paramJobCategory().length > 0 && props.highlightedGold) {
			params["scope[]"] = "highlightedGold";
			params["limit"] = "19";
			Object.keys(params).forEach((key) => url.searchParams.append(key, params[key]));

			const requestUrl = `${url}${paramJobCategory()}`;
			const response = await $fetch<{ data: CarouselJob[] }>(requestUrl);

			fetchedJobs = response?.data || [];
			jobsCache.value[jobsCacheKey] = [...fetchedJobs];

			return { banner: fetchedBanner, jobs: fetchedJobs }; // RETURN the data
		}

		// Standard Jobs
		params["limit"] = "20";
		if (props.highlightedGold) params["scope[]"] = "highlightedGold";
		Object.keys(params).forEach((key) => url.searchParams.append(key, params[key]));

		const mainResponse = await $fetch<{ data: CarouselJob[] }>(url.toString());
		let finalData = mainResponse?.data || [];

		// Extra Jobs
		if (props.dataUrlExtra) {
			const extraRequestUrl = `${globalConfig.API}/${props.dataUrlExtra}&region=${getRegionCode()}`;
			const extraResponse = await $fetch<{ data: CarouselJob[] }>(extraRequestUrl);
			finalData = Object.assign(extraResponse?.data || [], finalData);
		}

		fetchedJobs = finalData;
		jobsCache.value[jobsCacheKey] = [...fetchedJobs];

		// RETURN the final data payload
		return { banner: fetchedBanner, jobs: fetchedJobs };
	},
	{ server: true }
);

if (fetchedData.value) {
	banner.value = fetchedData.value.banner;
	jobsData.value = fetchedData.value.jobs;
}

// Evaluate fetch results
fetchError.value = !!asyncFetchError.value;
loading.value = false;
emit("loaded");

if (jobsData.value.length > 0 && paramJobCategory().length > 0 && props.highlightedGold) {
	emit("hasCategoryJobs");
}

if (fetchError.value || jobsData.value.length < 1) {
	emit("hideSection");
}

// --- Computed Data ---
const carouselData = computed(() => {
	let array: CarouselJob[] = [];

	if (props.rawJobsData.length != 0) {
		array.push(...(props.rawJobsData as CarouselJob[]));
	} else if (jobsData.value.length != 0) {
		array = [...jobsData.value];
	}

	if (!isMobile() && props.showBanner && Object.keys(banner.value).length > 0) {
		array.unshift({} as CarouselJob);
	}

	if (props.filterJobId) {
		array = array.filter((job) => job.id != props.filterJobId);
	}

	if (props.sortByGold) {
		array = array.sort((a, b) => {
			if (a.company && b.company) return Number(b.company.has_gold || 0) - Number(a.company.has_gold || 0);
			return 0;
		});
	}

	return array;
});

const ssrStaticJobs = computed(() => {
	let array: CarouselJob[] =
		props.rawJobsData.length != 0 ? [...(props.rawJobsData as CarouselJob[])] : [...jobsData.value];

	if (props.filterJobId) {
		array = array.filter((job) => job.id != props.filterJobId);
	}

	if (props.sortByGold) {
		array = array.sort((a, b) => Number(b.company?.has_gold || 0) - Number(a.company?.has_gold || 0));
	}

	return array.slice(0, 8);
});

// --- Carousel Logic ---
const settings = {
	itemsToShow: 1,
	snapAlign: "start",
	mouseDrag: true,
	touchDrag: true,
	transition: 800,
};

function getValidSlideWidth() {
	const contWidth = getDjContainerWidth();
	return contWidth > props.slideWidth ? props.slideWidth : contWidth;
}

function getItemsNumber() {
	return bodyWidth / (getValidSlideWidth() + slideMargin.value);
}

function showCarouselAddons() {
	const containerWidth = getDjContainerWidth();
	return (
		(bodyWidth - (bodyWidth - containerWidth) / 2) / (getValidSlideWidth() + slideMargin.value) <
		carouselData.value.length + 10
	);
}

function slideTo(i: number, maxIndex: number) {
	carouselEl.value?.slideTo(i <= maxIndex ? i : maxIndex);
}

function slidePrev(currentSlide: number, containerSlideNumber: number) {
	let goToIndex =
		currentSlide -
		(currentSlide % containerSlideNumber == 0 ? containerSlideNumber : currentSlide % containerSlideNumber);
	carouselEl.value?.slideTo(goToIndex);
}

function activePagination(
	index: number,
	currentSlide: number,
	slidesCount: number,
	containerSlideNumber: number,
	helperSlides: number
) {
	const lastPagination = index == Math.ceil((slidesCount - helperSlides) / containerSlideNumber) - 1;
	if (lastPagination && Math.ceil(currentSlide) >= slidesCount - containerSlideNumber - helperSlides) {
		return true;
	} else if (
		Math.floor(currentSlide / containerSlideNumber) == index &&
		Math.ceil(currentSlide) < slidesCount - containerSlideNumber - helperSlides
	) {
		return true;
	}
	return false;
}

function calculateCarouselParams() {
	let containerWidth = getDjContainerWidth();
	containerSlideNumber.value = Math.floor(containerWidth / getValidSlideWidth());
	bodyWidth = getBodyWidth();
	slideMargin.value =
		containerSlideNumber.value > 1
			? (containerWidth - containerSlideNumber.value * getValidSlideWidth()) / (containerSlideNumber.value - 1)
			: 20;
	settings.itemsToShow = getItemsNumber();
	helperSlides.value =
		Math.ceil((bodyWidth - containerWidth) / 2 / (getValidSlideWidth() + slideMargin.value)) +
		(bodyWidth > 1800 ? 1 : 0);
}

let doResize: ReturnType<typeof setTimeout> | undefined = undefined;

function isResized() {
	showCarousel.value = false;
	setTimeout(() => {
		calculateCarouselParams();
		showCarousel.value = true;
	}, 500);
}

function handleResize() {
	if (doResize) clearTimeout(doResize);
	bodyWidth != getBodyWidth() ? (doResize = setTimeout(isResized, 500)) : null;
}

// --- Lifecycle ---
onMounted(() => {
	calculateCarouselParams();
	showCarousel.value = true;
	window.addEventListener("resize", handleResize);
});
</script>
